<script>
import Chartist from 'chartist'
import pointLabels from './point-labels'
import uuid from 'uuid'

export default {
  name: 'NtvBarChart',

  props: {
    isInverted: {
      type: Boolean,
      default: false
    },
    labels: {
      type: Array,
      default () {
        return []
      }
    },
    series: {
      type: Array,
      default () {
        return [[]]
      }
    },
    width: {
      type: Number,
      default: undefined
    },
    /**
     * Width of an individual bar
     */
    barWidth: {
      type: Number,
      default: 20
    },
    horizontal: {
      type: Boolean,
      default: false
    },
    showPointLabels: {
      type: Boolean,
      default: false
    },
    /**
     * The options object passed to chartist.js. View all available options here
     * https://gionkunz.github.io/chartist-js/api-documentation.html#chartistbar-declaration-defaultoptions
     */
    options: {
      type: Object,
      default () {
        return {}
      }
    },
    /**
     * How long the whole chart animation takes.
     */
    animationDuration: {
      type: String,
      default: '0.3s'
    },
    /**
     * The delay before each bar animation.
     */
    animationBegin: {
      type: Number,
      default: 35
    },
    /**
     * The animation easing.
     */
    animationEasing: {
      type: String,
      default: 'easeInOutQuad'
    },
    total: {
      type: Number,
      default: null
    },
    showPercentages: {
      type: Boolean,
      default: false
    },
    /**
     * Whether or not to show a totals tooltip when a user hovers over one of the bars.
     */
    showTooltip: {
      type: Boolean,
      default: true
    },
    /**
     * @param barsClickable
     * @type Boolean
     * @default false
     * @description Whether or not bars should fire click events.
     */
    barsClickable: {
      type: Boolean,
      default: false
    }
  },

  mounted () {
    // Generate a random, unique id to use when create event listeners for bars.
    this.id_ = uuid
      .v4()
      .split('-')
      .join('')
      .replace(/\d/g, '') // HTML id attributes cannot start with a number.
    this.initChart()
  },

  data () {
    return {
      chartInstance: null,
      id_: null,
      hoveredBarIdx: null,
      showTotalTooltip: false,
      totalTooltipWidth: 0,
      widestLabel: 25
    }
  },

  computed: {
    totalTooltipContent () {
      if (this.hoveredBarIdx === null || this.total === null) {
        return 0
      }

      const barCount = this.barCount()
      const idx = barCount - 1 - this.hoveredBarIdx
      let values = []

      if (this.series.length > 1) {
        values = this.series.map(series => series[idx])
      } else {
        values = [this.series[0][idx]]
      }

      return values.map(value => {
        let formattedValue = value
        let formattedTotal = this.total

        if (
          this.options.pointLabels &&
          this.options.pointLabels.labelInterpolationFnc
        ) {
          formattedValue = this.options.pointLabels.labelInterpolationFnc(
            value
          )
          formattedTotal = this.options.pointLabels.labelInterpolationFnc(
            this.total
          )
        }

        return `${formattedValue} of ${formattedTotal}`
      })
    },
    tooltipVerticalOffset () {
      if (this.hoveredBarIdx === null || this.total === null) {
        return 0
      }

      const barCount = this.barCount()
      const idx = barCount - 1 - this.hoveredBarIdx

      if (idx === 0) {
        return -40
      } else {
        return (idx - 1) * 40
      }
    },
    tooltipHorizontalOffset () {
      if (this.hoveredBarIdx === null || this.total === null) {
        return 0
      }

      const bars = this.$refs.chart.querySelectorAll('.ct-bar')
      const bar = bars[this.hoveredBarIdx]

      if (!bar) {
        return 0
      }

      const lineLength =
        Math.abs(parseInt(bar.attributes.x1.value) - parseInt(bar.attributes.x2.value))

      return (lineLength / 2) - (this.totalTooltipWidth / 2) + 25 // prettier-ignore
    },
    cOptions () {
      const { series, total } = this.$props

      const axisX =
        this.options && this.options.axisX ? this.options.axisX : {}
      const axisY =
        this.options && this.options.axisY ? this.options.axisY : {}

      const seriesCount = series ? series.length : 0
      const singleSeriesCount = series[0] ? series[0].length : 0

      const options = {
        // seriesBarDistance: 15,
        horizontalBars: this.horizontal,
        width: this.width,
        height: this.barWidth * 2 * singleSeriesCount * seriesCount + 'px',
        // We need to reverse the data since we want to match the order of the
        // labels to the order of the chart data.
        reverseData: true,
        ...this.options,
        axisX: {
          labelInterpolationFnc: val => (this.isInverted ? -val : val),
          ...axisX
        },
        axisY: {
          position: this.isInverted ? 'end' : 'start',
          ...axisY
        },
        plugins: [
          this.showPointLabels &&
            pointLabels({
              align: 'right',
              isInverted: this.isInverted,
              animationDuration: this.animationDuration,
              animationBegin: this.animationBegin,
              animationEasing: this.animationEasing,
              ...this.options.pointLabels,
              labelInterpolationFnc: val => {
                const value = this.isInverted ? -val : val
                const formattedValues = {
                  value,
                  percentage: null
                }

                if (
                  this.options.pointLabels &&
                  this.options.pointLabels.labelInterpolationFnc
                ) {
                  formattedValues.value = this.options.pointLabels.labelInterpolationFnc(
                    value
                  )
                }

                if (this.showPercentages && total) {
                  const percentage = total !== 0 ? value / total : 0
                  formattedValues.percentage = `(${
                    Math.round(percentage * 100) !== 0
                      ? Math.round(percentage * 100)
                      : '<1'
                  }%)`
                }

                return formattedValues
              }
            })
        ],
        // Chart padding needs to be dynamic based upon the width of the widest point label otherwise, the point
        // labels will overflow the SVG container.
        chartPadding: {
          top: 0,
          bottom: 0,
          left: this.isInverted ? this.widestLabel + 25 : 25,
          right: this.isInverted ? 25 : this.widestLabel + 25,
          ...this.options.chartPadding
        }
      }

      return options
    }
  },

  watch: {
    labels (newValue, oldValue) {
      if (newValue !== oldValue) {
        this.updateChartData({
          labels: newValue,
          series: this.series
        })
      }
    },
    series (newValue, oldValue) {
      if (newValue !== oldValue) {
        this.updateChartData({
          labels: this.labels,
          series: newValue
        })
      }
    },
    total () {
      this.initChart()
    },
    showTotalTooltip () {
      if (this.showTooltip) {
        this.$nextTick(() => {
          this.totalTooltipWidth = this.$refs.tooltip.offsetWidth
        })
      }
    }
  },

  methods: {
    barCount () {
      return this.$refs.chart.querySelectorAll('.ct-bar').length
    },
    initChart () {
      const { labels, series } = this.$props

      this.chartInstance = new Chartist.Bar(
        this.$refs.chart,
        { labels, series },
        this.cOptions
      ).on('draw', data => {
        if (data.type === 'bar') {
          data.element.attr({
            style: `stroke-width: ${this.barWidth}px`
          })

          data.element.animate({
            x2: {
              // Because we are inverting the data for a horizontal chart, the
              // reverse order of animation needs to be set.
              begin: (data.series.length - data.index) * this.animationBegin,
              dur: this.animationDuration,
              from: data.x1,
              to: data.x2,
              easing: this.animationEasing
            }
          })
        }
      })

      this.chartInstance.on('created', () => {
        const bars = this.$refs.chart.querySelectorAll(`line.ct-bar`)
        const pointLabels = Array.from(this.$refs.chart.querySelectorAll('text.ct-point-label:not(.percent)'))
        this.widestLabel = pointLabels
          .map(pl => {
            const previousSibling = pl.previousSibling
            const nextSibling = pl.nextSibling

            if (previousSibling.classList.contains('percent')) {
              return pl.getBBox().width + previousSibling.getBBox().width + 10
            } else if (nextSibling.classList.contains('percent')) {
              return pl.getBBox().width + previousSibling.getBBox().width + 10
            } else {
              return pl.getBBox().width + 10
            }
          })
          .reduce((acc, value) => {
            return value > acc ? value : acc
          }, 0)

        bars.forEach((bar, idx) => {
          const mouseEnterHandler = () => {
            this.hoveredBarIdx = idx
            this.showTotalTooltip = true
          }
          const mouseLeaveHandler = () => {
            this.showTotalTooltip = false
            this.hoveredBarIdx = null
          }

          bar.onmouseenter = mouseEnterHandler
          bar.onmouseleave = mouseLeaveHandler
          bar.nextSibling.onmouseenter = mouseEnterHandler
          bar.nextSibling.onmouseleave = mouseLeaveHandler

          if (this.barsClickable) {
            // Since the indexes have been reversed to show up in the correct order, we need to unreverse them to
            // return the correct idx for the data that the clicked bar represents.
            const clickHandler = () => { this.$emit('barClick', Math.abs(idx - bars.length + 1)) }
            bar.onclick = clickHandler
          }
        })
      })

      if (this.isInverted) {
        this.chartInstance.on('data', context => {
          context.data.series = context.data.series.map(series => {
            return series.map(val => -val)
          })
        })
      }
    },
    updateChartData (data) {
      this.chartInstance &&
        this.chartInstance.update(data, this.cOptions, true)
    }
  }
}
</script>

<template>
  <div
    ref="chart"
    class="ntv-bar-chart"
    :id="id_"
    :class="{
      'clickable': barsClickable
    }"
  >
    <div
      class="chartTooltip"
      v-if="showTooltip"
      v-show="showTotalTooltip"
      :style="{
        top: `${tooltipVerticalOffset}px`,
        right: this.isInverted ? `${tooltipHorizontalOffset}px` : null,
        left: !this.isInverted ? `${tooltipHorizontalOffset}px` : null
      }"
      ref="tooltip"
    >
      <p
        class="chartTooltip-content"
        v-for="(val, idx) in totalTooltipContent"
        :key="idx"
      >
        {{ val }}
      </p>
      <div class="chartTooltip-arrow" />
    </div>
  </div>
</template>

<style lang="scss">
@import './scss/chartist.scss';

.ntv-bar-chart {
  & > svg {
    overflow: visible;
  }

  &.clickable {
    .ct-bar {
      &:hover {
        cursor: pointer;
      }
    }
  }
}
</style>

<style lang="scss" scoped>
.ntv-bar-chart {
  position: relative;
}
.chartTooltip {
  position: absolute;
  background: white;
  border-radius: 3px;
  padding: 10px 13px;
  -webkit-box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.5);
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.5);

  &-content {
    padding: 0;
    margin: 0;
  }

  &-arrow {
    width: 10px;
    height: 10px;
    position: absolute;
    overflow: hidden;
    bottom: -8px;
    left: calc(50% - 6px);
    margin-top: 0;
    margin-bottom: 0;

    &::after {
      content: '';
      position: absolute;
      width: 10px;
      height: 10px;
      background: white;
      -webkit-transform: translateX(-50%) translateY(-50%) rotate(45deg);
      transform: translateX(-50%) translateY(-50%) rotate(45deg);
      top: 0;
      left: 50%;
      box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.5);
    }
  }
}
</style>
